import React, { useEffect, useState } from "react";
import { Modal, Button, Table, Badge } from "react-bootstrap";
import axios from "axios";
import config from "../../config/config";
import { getAuthToken } from "../../utils/token";
import { toast } from "react-toastify";

function SetMachineUtilization({ show, handleClose }) {
    const [machines, setMachines] = useState([]);
    const [loading, setLoading] = useState(false);
    const [overallUtilization, setOverallUtilization] = useState(0);
    const getUtilizationClass = (value) => {
        const utilization = Number(value || 0);
        if (utilization < 50) return "bg-success";
        if (utilization < 75) return "bg-warning";
        return "bg-danger";
    };
    useEffect(() => {
        if (show) getMachineUtilization();
    }, [show]);

    const getMachineUtilization = async () => {
        const token = getAuthToken();

        if (!token) {
            toast.error("Authentication token not found.");
            return;
        }

        try {
            setLoading(true);

            const { data } = await axios.get(
                `${config.API}/senmanager/allmachinesutilization`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            if (data?.success) {
                setMachines(Array.isArray(data.data) ? data.data : []);
                const utilizations = data.data
                    .map(item => Number(item.machine_utilization))
                    .filter(value => value > 0);

                const averageUtilization = utilizations.length
                    ? utilizations.reduce((sum, value) => sum + value, 0) / utilizations.length
                    : 0;

                setOverallUtilization(averageUtilization.toFixed(2));
            } else {
                setMachines([]);
                setOverallUtilization(0);
                toast.warning(
                    data?.message || "No machine utilization data found."
                );
            }
        } catch (error) {
            console.error("Machine Utilization Error:", error);
            setMachines([]);
            setOverallUtilization(0);
            toast.error(
                error.response?.data?.message ||
                "Unable to load machine utilization."
            );
        } finally {
            setLoading(false);
        }
    };

    const getInitials = (name = "") =>
        name
            .split(" ")
            .filter(Boolean)
            .map((item) => item[0])
            .join("")
            .slice(0, 2)
            .toUpperCase() || "-";

    const activeWorkers = new Set(
        machines.map((item) => item?.user_id).filter(Boolean)
    ).size;
    return (
        <Modal
            show={show}
            onHide={handleClose}
            centered
            size="xl"
            className="font_family"
        >
            <Modal.Header className="border-0 px-4 pt-4 pb-2">
                <div>
                    <h4 className="fw-bold mb-1">Machine Utilization</h4>
                    <p className="text-muted mb-0">
                        View machine utilization, assigned workers and
                        current production.
                    </p>
                </div>

                <button
                    type="button"
                    className="btn-close"
                    onClick={handleClose}
                    aria-label="Close"
                />
            </Modal.Header>

            <Modal.Body className="px-4 pb-4">
                {/* Summary */}
                <div className="bg-light rounded-4 p-3 mb-4">
                    <div className="row g-3">

                        {/* Total Machines */}
                        <div className="col-12 col-md-4">
                            <div
                                className="bg-white rounded-4 border shadow-sm p-3 h-100"
                                style={{
                                    transition: "all .25s ease",
                                    animation: "fadeInUp .5s ease",
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = "translateY(-4px)";
                                    e.currentTarget.style.boxShadow =
                                        "0 10px 25px rgba(0,0,0,.08)";
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = "translateY(0)";
                                    e.currentTarget.style.boxShadow =
                                        "0 .125rem .25rem rgba(0,0,0,.075)";
                                }}
                            >
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <small className="text-muted fw-bold">
                                            TOTAL MACHINES
                                        </small>

                                        <div className="fs-3 fw-bold text-dark mt-1">
                                            {machines.length}
                                        </div>

                                        <small className="text-muted">
                                            Registered machines
                                        </small>
                                    </div>

                                    <div
                                        className="rounded-4 bg-primary bg-opacity-10 text-primary d-flex align-items-center justify-content-center"
                                        style={{
                                            width: "52px",
                                            height: "52px",
                                            fontSize: "22px",
                                        }}
                                    >
                                        <i className="bi bi-cpu-fill"></i>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Machine Utilization */}
                        <div className="col-12 col-md-4">
                            <div
                                className="bg-white rounded-4 border shadow-sm p-3 h-100"
                                style={{
                                    transition: "all .25s ease",
                                    animation: "fadeInUp .6s ease",
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = "translateY(-4px)";
                                    e.currentTarget.style.boxShadow =
                                        "0 10px 25px rgba(0,0,0,.08)";
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = "translateY(0)";
                                    e.currentTarget.style.boxShadow =
                                        "0 .125rem .25rem rgba(0,0,0,.075)";
                                }}
                            >
                                <div className="d-flex justify-content-between align-items-center">
                                    <div className="flex-grow-1">
                                        <small className="text-muted fw-bold">
                                            MACHINE UTILIZATION
                                        </small>

                                        <div className="fs-3 fw-bold text-dark mt-1">
                                            {overallUtilization}%
                                        </div>

                                        <div
                                            className="progress mt-2"
                                            style={{
                                                height: "5px",
                                                maxWidth: "160px",
                                            }}
                                        >
                                            <div
                                                className="progress-bar bg-warning"
                                                style={{
                                                    width: `${Math.min(
                                                        Number(overallUtilization || 0),
                                                        100
                                                    )}%`,
                                                    transition: "width 1s ease",
                                                }}
                                            />
                                        </div>

                                        <small className="text-muted">
                                            Overall performance
                                        </small>
                                    </div>

                                    <div
                                        className="rounded-4 bg-warning bg-opacity-10 text-warning d-flex align-items-center justify-content-center"
                                        style={{
                                            width: "52px",
                                            height: "52px",
                                            fontSize: "22px",
                                        }}
                                    >
                                        <i className="bi bi-speedometer2"></i>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Active Workers */}
                        <div className="col-12 col-md-4">
                            <div
                                className="bg-white rounded-4 border shadow-sm p-3 h-100"
                                style={{
                                    transition: "all .25s ease",
                                    animation: "fadeInUp .7s ease",
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = "translateY(-4px)";
                                    e.currentTarget.style.boxShadow =
                                        "0 10px 25px rgba(0,0,0,.08)";
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = "translateY(0)";
                                    e.currentTarget.style.boxShadow =
                                        "0 .125rem .25rem rgba(0,0,0,.075)";
                                }}
                            >
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <small className="text-muted fw-bold">
                                            ACTIVE WORKERS
                                        </small>

                                        <div className="fs-3 fw-bold text-success mt-1">
                                            {activeWorkers}
                                        </div>

                                        <small className="text-muted">
                                            Currently deployed
                                        </small>
                                    </div>

                                    <div
                                        className="rounded-4 bg-success bg-opacity-10 text-success d-flex align-items-center justify-content-center"
                                        style={{
                                            width: "52px",
                                            height: "52px",
                                            fontSize: "22px",
                                        }}
                                    >
                                        <i className="bi bi-people-fill"></i>
                                    </div>
                                </div>
                            </div>
                        </div>

                    </div>

                    {/* Inline animation */}
                    <style>
                        {`
            @keyframes fadeInUp {
                from {
                    opacity: 0;
                    transform: translateY(10px);
                }
                to {
                    opacity: 1;
                    transform: translateY(0);
                }
            }
        `}
                    </style>
                </div>

                {/* Table Header */}
                <div className="d-flex align-items-center justify-content-between mb-3">
                    <div>
                        <h6 className="fw-bold mb-1">
                            Machine Utilization Details
                        </h6>
                        <small className="text-muted">
                            Current machine allocation and production status
                        </small>
                    </div>

                    <Badge bg="dark" className="px-3 py-2">
                        Live Data
                    </Badge>
                </div>

                {/* Table */}
                <div className="border rounded-3 overflow-hidden">
                    <div className="table-responsive">
                        <Table hover className="mb-0 align-middle">
                            <thead className="table-light">
                                <tr>
                                    <th className="px-3 py-3">Machine</th>
                                    <th className="px-3 py-3">Product</th>
                                    <th className="px-3 py-3">
                                        Utilization
                                    </th>
                                    <th className="px-3 py-3">Worker</th>
                                </tr>
                            </thead>

                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td
                                            colSpan="5"
                                            className="text-center py-5"
                                        >
                                            <div
                                                className="spinner-border text-primary"
                                                role="status"
                                            />
                                            <div className="text-muted mt-2">
                                                Loading machine utilization...
                                            </div>
                                        </td>
                                    </tr>
                                ) : machines.length ? (
                                    machines.map((item, index) => {
                                        const utilization = Number(
                                            item?.machine_utilization || 0
                                        );

                                        return (
                                            <tr
                                                key={
                                                    item?.machine_id ?? index
                                                }
                                            >
                                                <td className="px-3 py-3">
                                                    <span className="fw-semibold d-block">
                                                        {item?.machine?.line ||
                                                            "-"}
                                                    </span>
                                                    <small className="text-muted">
                                                        Capacity per shift:{" "}
                                                        {item?.product?.name ? item?.machine?.capacity ?? 0 : "Setup Die First"}
                                                    </small>
                                                </td>
                                                <td className="px-3 py-3">
                                                    <span className="fw-semibold text-dark">
                                                        {item?.product?.name ||
                                                            "Machine Not Set"}
                                                    </span>
                                                </td>
                                                <td className="px-3 py-3">
                                                    <div
                                                        className="d-flex align-items-center gap-3"
                                                        style={{ minWidth: "240px" }}
                                                    >
                                                        {/* Progress Area */}
                                                        <div className="flex-grow-1">

                                                            <div className="d-flex justify-content-between align-items-center mb-1">
                                                                <small className="text-muted fw-medium">
                                                                    Utilization
                                                                </small>

                                                                <small className="fw-bold text-dark">
                                                                    {utilization.toFixed(2)}%
                                                                </small>
                                                            </div>

                                                            <div
                                                                className="progress bg-light"
                                                                style={{
                                                                    height: "7px",
                                                                    borderRadius: "50px",
                                                                }}
                                                            >
                                                                <div
                                                                    className={`progress-bar ${getUtilizationClass(
                                                                        utilization
                                                                    )}`}
                                                                    role="progressbar"
                                                                    style={{
                                                                        width: `${Math.min(
                                                                            Math.max(utilization, 0),
                                                                            100
                                                                        )}%`,
                                                                        borderRadius: "50px",
                                                                        transition:
                                                                            "width 0.5s ease-in-out",
                                                                    }}
                                                                    aria-valuenow={utilization}
                                                                    aria-valuemin="0"
                                                                    aria-valuemax="100"
                                                                />
                                                            </div>

                                                            {/* Produced */}
                                                            {item?.total_produced > 0 && (
                                                                <div className="mt-1">
                                                                    <small
                                                                        className="text-muted"
                                                                        style={{
                                                                            fontSize: "10px",
                                                                        }}
                                                                    >
                                                                        <i className="bi bi-box-seam me-1"></i>
                                                                        {item.total_produced.toLocaleString()}{" "}
                                                                        produced
                                                                    </small>
                                                                </div>
                                                            )}

                                                        </div>
                                                    </div>
                                                </td>

                                                <td className="px-3 py-3">
                                                    <div className="d-flex align-items-center gap-2">
                                                        <div
                                                            className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center"
                                                            style={{
                                                                width: "32px",
                                                                height: "32px",
                                                                fontSize: "12px",
                                                                flexShrink: 0,
                                                            }}
                                                        >
                                                            {item?.user?.name
                                                                ? getInitials(item.user.name)
                                                                : "UN"}
                                                        </div>

                                                        <span className="fw-semibold">
                                                            {item?.user?.name || "User Not Set"}
                                                        </span>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })
                                ) : (
                                    <tr>
                                        <td
                                            colSpan="5"
                                            className="text-center text-muted py-5"
                                        >
                                            No machine utilization data found.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>
                    </div>
                </div>
            </Modal.Body>

            <Modal.Footer className="border-0 px-4 pb-4 pt-0">
                <Button variant="dark" className="px-4" onClick={handleClose}>
                    Close
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

export default SetMachineUtilization;